import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CandidateDetailsCard } from 'modules/hiring/shared/components';
import { CANDIDATE_STATUS } from 'modules/hiring/shared/constants';
import { useCandidate } from 'modules/hiring/shared/hooks';
import { rejectCandidate } from 'modules/hiring/shared/service';
import { getCandidateStatusMeta } from 'modules/hiring/shared/utils';
import {
  ApproveRejectActions,
  BaseTextField,
  DateTimeInput,
  Loader,
  SectionTitle,
} from 'modules/shared/components';
import { logError } from 'utils/logger';
import { scheduleInterview } from '../candidates.service';

/**
 * UI action modes for candidate detail workflow.
 * Controls which editor or action panel is currently active.
 */
export const CANDIDATE_ACTION_MODE = Object.freeze({
  IDLE: 'idle' as const,
  SCHEDULE: 'schedule' as const,
  RESCHEDULE: 'reschedule' as const,
  REJECT: 'reject' as const,
});

const CANDIDATE_STATUS_QUERY = Object.freeze({
  SCHEDULED: 'scheduled',
  REJECTED: 'rejected',
});

/**
 * CandidateDetails
 *
 * Detail and action view for a single candidate application.
 *
 * Responsibilities:
 * - Display candidate profile and current hiring status
 * - Schedule or reschedule interviews
 * - Reject candidate with validated reason
 * - Coordinate side effects (navigation, refresh, notifications)
 *
 * This component owns:
 * - UI action state (schedule / reschedule / reject)
 * - Non-RHF form state for interview date and rejection reason
 *
 * It does NOT:
 * - Perform data shaping (delegated to services/hooks)
 * - Manage global state
 */
export default function CandidateDetails() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { refreshCandidates } = useOutletContext<Record<string, any>>();

  const { candidate, loading, refresh } = useCandidate(applicationId);
  const { name, currentInterviewRound } = candidate || {};

  const [actionMode, setActionMode] = useState<string>(CANDIDATE_ACTION_MODE.IDLE);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const [interviewAt, setInterviewAt] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const trimmedRejectionReason = rejectionReason.trim();
  const rejectionReasonLength = trimmedRejectionReason.length;

  const rejectionError =
    rejectionReasonLength === 0
      ? null
      : rejectionReasonLength < 3
        ? 'Minimum 3 characters required'
        : rejectionReasonLength > 300
          ? 'Rejection reason cannot exceed 300 characters'
          : null;

  const isRejectionInvalid = Boolean(rejectionError);
  const canSubmitRejection = !saving && !isRejectionInvalid && rejectionReasonLength > 0;

  const statusMeta = candidate ? getCandidateStatusMeta(candidate) : null;

  const isNew = statusMeta?.status === CANDIDATE_STATUS.APPLIED;
  const isScheduled = statusMeta?.status === CANDIDATE_STATUS.SCHEDULED;
  const isRejected = statusMeta?.status === CANDIDATE_STATUS.REJECTED;

  const canEditInterview = isNew || isScheduled;
  const isEditingInterview =
    actionMode === CANDIDATE_ACTION_MODE.SCHEDULE ||
    actionMode === CANDIDATE_ACTION_MODE.RESCHEDULE;

  const resetActionState = () => {
    setActionMode(CANDIDATE_ACTION_MODE.IDLE);
    setRejectionReason('');
    setHasSubmitted(false);
  };

  useEffect(() => {
    if (candidate?.nextInterview) {
      setInterviewAt(dayjs(candidate.nextInterview));
    }
  }, [candidate?.nextInterview]);

  const handleSaveInterview = async () => {
    setHasSubmitted(true);
    if (!interviewAt) return;
    setSaving(true);

    try {
      await scheduleInterview(applicationId, {
        interviewAt: interviewAt.toISOString(),
      });

      toast.success(
        actionMode === CANDIDATE_ACTION_MODE.RESCHEDULE
          ? 'Interview rescheduled'
          : 'Interview scheduled',
      );

      resetActionState();
      refreshCandidates();
      refresh();
      navigate(`/candidates/${applicationId}?status=${CANDIDATE_STATUS_QUERY.SCHEDULED}`, {
        replace: true,
      });
    } catch (error) {
      toast.error(
        logError({
          error,
          message: 'Failed to save interview',
          code: 'INTERVIEW_SAVE_FAILED',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRejectCandidate = async () => {
    if (isRejectionInvalid || !rejectionReasonLength) return;
    try {
      await rejectCandidate(applicationId, { reason: rejectionReason });
      toast.success('Candidate rejected');
      resetActionState();
      refreshCandidates();
      navigate(`/candidates/${applicationId}?status=${CANDIDATE_STATUS_QUERY.REJECTED}`, {
        replace: true,
      });
      refresh();
    } catch (error) {
      toast.error(
        logError({
          error: error,
          message: 'Failed to reject candidate',
          code: 'CANDIDATE_REJECT_FAILED',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const interviewEditor =
    canEditInterview && isEditingInterview ? (
      <DateTimeInput
        id='interviewAt'
        value={interviewAt}
        onChange={setInterviewAt}
        disabled={saving}
        error={hasSubmitted && !interviewAt}
        errorMessage='Interview date & time is required'
        orientation='row'
        disablePast
        minDate={dayjs()}
        views={['day']}
        format='DD/MM/YYYY'
      />
    ) : null;

  const interviewLinkLabel =
    Number(currentInterviewRound) === 0 ? 'Start interview process' : 'Resume interview process';

  return loading ? (
    <Loader />
  ) : !candidate ? (
    <Alert severity='error'>Candidate not found. Please try again</Alert>
  ) : (
    <>
      <Divider />
      <div className='column gap-3'>
        <SectionTitle
          variant='subtitle'
          title={`${name} Details`}
          meta={
            <Typography color={statusMeta.color} fontWeight={500}>
              {statusMeta.label}
            </Typography>
          }
        />
        <CandidateDetailsCard candidate={candidate ?? {}} interviewEditor={interviewEditor} />
        {isNew && !isEditingInterview && (
          <ApproveRejectActions
            onApprove={() => setActionMode(CANDIDATE_ACTION_MODE.SCHEDULE)}
            onReject={() => setActionMode(CANDIDATE_ACTION_MODE.REJECT)}
            loading={saving}
          />
        )}
        {isScheduled && !isEditingInterview && (
          <div className='row gap-3 self-end'>
            <button
              type='button'
              className='btn cta-text-btn text-semibold underline'
              onClick={() => {
                setInterviewAt(dayjs(candidate.nextInterview));
                setActionMode(CANDIDATE_ACTION_MODE.RESCHEDULE);
              }}
            >
              Reschedule Interview
            </button>

            <Link to={`/candidates/${applicationId}/interview`} className='btn cta-outlined-btn'>
              {interviewLinkLabel}
            </Link>
          </div>
        )}
        {isEditingInterview && (
          <div className='row gap-3 self-end'>
            <button
              type='button'
              className='btn text-btn underline'
              disabled={saving}
              onClick={resetActionState}
            >
              Cancel
            </button>
            <button
              type='button'
              className='btn cta-outlined-btn'
              disabled={saving || !interviewAt}
              onClick={handleSaveInterview}
            >
              Save
            </button>
          </div>
        )}
        {actionMode === CANDIDATE_ACTION_MODE.REJECT && (
          <div className='row gap-3'>
            <BaseTextField
              id='candidate-rejection-reason'
              value={rejectionReason}
              onChange={setRejectionReason}
              disabled={saving}
              placeholder='Reason for rejection'
              fullWidth
              label='Reason for rejection'
              inputProps={{
                minLength: 3,
                maxLength: 300,
                required: actionMode === CANDIDATE_ACTION_MODE.REJECT,
              }}
              error={hasSubmitted && isRejectionInvalid}
              errorMessage={hasSubmitted ? rejectionError : null}
            />
            <div className='row gap-3 self-end'>
              <button type='button' className='btn text-btn underline' onClick={resetActionState}>
                Cancel
              </button>
              <button
                type='button'
                className='btn cta-outlined-btn'
                disabled={!canSubmitRejection}
                onClick={handleRejectCandidate}
              >
                Save
              </button>
            </div>
          </div>
        )}
        {isRejected && (
          <div className='row row--between'>
            <div className='data-grid-column'>
              <div className='data-grid-row'>
                <span className='label--secondary'>Reason for rejection</span>
                <span className='value'>{candidate.rejectionReason}</span>
              </div>
            </div>
            {statusMeta?.status === CANDIDATE_STATUS.REJECTED && statusMeta.roundLabel && (
              <div className='data-grid-column'>
                <div className='data-grid-row'>
                  <span className='label--secondary'>Status</span>
                  <span className='value'>{statusMeta.roundLabel}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
